import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { MessageCircle, Mail, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { COMPANY, whatsappUrl, mailtoUrl } from "@/lib/contact";

const appliances = [
  "Air Conditioner",
  "Refrigerator",
  "Television",
  "Washing Machine",
  "Water Heater",
  "Microwave",
  "Computer / Office",
  "Lighting",
  "Pool Pump",
];

const schema = z.object({
  name: z.string().trim().min(2, "Name is too short").max(80),
  bill: z.string().max(20).optional(),
  battery: z.enum(["yes", "no", "maybe"]).default("maybe"),
  appliances: z.array(z.string()).max(20),
  notes: z.string().max(500).optional(),
});

function buildMessage(d: z.infer<typeof schema>) {
  const apps = d.appliances.length ? d.appliances.join(", ") : "Not specified";
  const battery = d.battery === "yes" ? "Yes" : d.battery === "no" ? "No" : "Not sure / advise me";
  return `Hello Green Volt LTD,

I would like to request a solar PV quotation.

• Name: ${d.name}
• Monthly electricity bill: ${d.bill ? `Rs ${d.bill}` : "Not specified"}
• Battery backup needed: ${battery}
• Main appliances: ${apps}${d.notes ? `\n• Notes: ${d.notes}` : ""}

Please advise on the best system for my needs.
Thank you.`;
}

export function QuotationForm() {
  const [data, setData] = useState({
    name: "",
    bill: "",
    battery: "maybe" as "yes" | "no" | "maybe",
    appliances: [] as string[],
    notes: "",
  });

  const toggleApp = (a: string) =>
    setData((d) => ({
      ...d,
      appliances: d.appliances.includes(a)
        ? d.appliances.filter((x) => x !== a)
        : [...d.appliances, a],
    }));

  const send = (channel: "whatsapp" | "email") => {
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please complete the form");
      return;
    }
    const msg = buildMessage(parsed.data);
    if (channel === "whatsapp") {
      window.open(whatsappUrl(msg), "_blank", "noopener,noreferrer");
    } else {
      window.location.href = mailtoUrl("Solar PV Quotation Request", msg);
    }
    toast.success("Opening your message…");
  };

  return (
    <section id="quotation" className="py-24 sm:py-32 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12 items-start">
        <div className="lg:col-span-2 lg:sticky lg:top-28">
          <span className="text-sm font-medium text-primary uppercase tracking-widest">
            Smart Quotation
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight">
            Tell us your needs.
            <br />
            Get a tailored quote.
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">
            Fill the form and we'll generate a ready-to-send message — straight to our WhatsApp or
            email. No spam, no obligation.
          </p>

          <div className="mt-8 space-y-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-3">
              <Calculator className="w-4 h-4 text-primary" />
              Sized to your real consumption
            </div>
            <div className="flex items-center gap-3">
              <MessageCircle className="w-4 h-4 text-primary" />
              Reply within a few hours
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 p-6 sm:p-8 rounded-3xl bg-card border border-border shadow-elegant">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
              placeholder="John Appavoo"
              className="mt-1.5"
            />
          </div>

          <div className="mt-4">
            <Label htmlFor="bill">Average monthly electricity bill (Rs)</Label>
            <Input
              id="bill"
              type="number"
              inputMode="numeric"
              value={data.bill}
              onChange={(e) => setData({ ...data, bill: e.target.value })}
              placeholder="e.g. 3500"
              className="mt-1.5"
            />
          </div>

          <div className="mt-6">
            <Label>Main appliances</Label>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {appliances.map((a) => {
                const active = data.appliances.includes(a);
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => toggleApp(a)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left border transition ${
                      active
                        ? "bg-primary/15 border-primary text-foreground"
                        : "bg-background border-border hover:border-primary/40"
                    }`}
                  >
                    <Checkbox checked={active} className="pointer-events-none" />
                    {a}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6">
            <Label>Battery backup?</Label>
            <RadioGroup
              value={data.battery}
              onValueChange={(v) => setData({ ...data, battery: v as typeof data.battery })}
              className="mt-3 grid grid-cols-3 gap-2"
            >
              {[
                { v: "yes", l: "Yes" },
                { v: "no", l: "No" },
                { v: "maybe", l: "Not sure" },
              ].map((o) => (
                <Label
                  key={o.v}
                  htmlFor={`bat-${o.v}`}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition ${
                    data.battery === o.v
                      ? "bg-primary/15 border-primary"
                      : "bg-background border-border hover:border-primary/40"
                  }`}
                >
                  <RadioGroupItem id={`bat-${o.v}`} value={o.v} className="sr-only" />
                  {o.l}
                </Label>
              ))}
            </RadioGroup>
          </div>

          <div className="mt-4">
            <Label htmlFor="notes">Additional notes (optional)</Label>
            <Input
              id="notes"
              value={data.notes}
              onChange={(e) => setData({ ...data, notes: e.target.value })}
              placeholder="Roof type, location, timeline…"
              className="mt-1.5"
            />
          </div>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => send("whatsapp")}
              size="lg"
              className="flex-1 h-12 text-base shadow-glow"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Send via WhatsApp
            </Button>
            <Button
              onClick={() => send("email")}
              size="lg"
              variant="outline"
              className="flex-1 h-12 text-base"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send via Email
            </Button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground text-center">
            We respond from {COMPANY.whatsappDisplay} · {COMPANY.email}
          </p>
        </div>
      </div>
    </section>
  );
}
